import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { filter, map } from 'rxjs/operators';
import { ActionService } from 'src/app/core/services/action.service';
import { RouteService } from 'src/app/core/services/route.service';
import {
  AddPage,
  LoadPage,
  UpdatePage
} from 'src/app/core/state/_actions/settings/pages.action';
import { OpTypes } from 'src/app/core/state/_models/general.model';
import {
  getPageDetail,
  getPagesOperations,
} from 'src/app/core/state/_selectors/settings/pages.selector';
import { AlertService, ToastTypes } from 'src/app/shared/services/alert.service';
import { SubSink } from 'subsink';
import { PageDetail } from '../pages.constants';
import { environment } from 'src/environments/environment';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';

const defaults = {
  markdown: '# Heading\n\nSome **bold** and _italic_ text\nBy [Scott Cooper](https://github.com/scttcper)',
  'text/typescript': `const component = {
                        name: "@ctrl/ngx-codemirror",
                        author: "Scott Cooper",
                        repo: "https://github.com/scttcper/ngx-codemirror"
                     };
                     const hello: string = '';`,
};

@Component({
  selector: 'app-upsert-page',
  templateUrl: './upsert-page.component.html',
  styleUrls: ['./upsert-page.component.scss'],
})
export class UpsertPageComponent implements OnInit, OnDestroy {
  @Input('opType') opType: string = '';
  @Input('pageDetail') pageDetail: PageDetail | null = null;
  pageKey: string | null = null;
  form!: UntypedFormGroup;
  defaults = defaults;
  mode: keyof typeof defaults = 'markdown';
  options = {
    lineNumbers: true,
    mode: this.mode,
  };
  body: any = '';
  subsink:any = new SubSink();
  type: 'editor' | 'builder' = 'editor';
  isUpsertPageComponent:boolean = true;
  ogInsightsUrl = environment.ogInsightsUrl;
  isOgInsightsEnabled = false;

  constructor(
    private fb: UntypedFormBuilder,
    private route: RouteService,
    private modal: NgbModal,
    private action: ActionService,
    private store: Store,
    private alert: AlertService,
    private readonly ldService: LaunchDarklyService
  ) {}

  ngOnInit(): void {
    this.pageKey = this.route.getPageKey() ? this.route.getPageKey() : null;
    this.initForm();
    if (this.pageKey)
      this.store.dispatch(
        new LoadPage(this.route.getApplicationKey(), this.pageKey)
      );
    this.loadSubscribers();
    this.subsink.add(
      this.ldService
        .getFlagValue(FEATURE_FLAGS.ENABLE_OG_INSIGHTS_CHARTS, false)
        .subscribe((value) => {
          this.isOgInsightsEnabled = !!value;
        })
    );
  }

  loadSubscribers = (): void => {
    this.subsink.sink = this.store
      .pipe(
        select(getPageDetail),
        filter(
          (pages) => !!pages[this.route.getApplicationKey()] && !!this.pageKey
        ),
        map((pages) => pages[this.route.getApplicationKey()])
      )
      .subscribe((pages) => {
        if (pages && pages[this.pageKey!]) {
          this.body = pages[this.pageKey!]['Body'];
          this.pageDetail = pages[this.pageKey!];
          this.initForm();
        }
      });
  };

  /**
   * Initialize the form
   */
  initForm = (): void => {
    this.form = this.fb.group({
      Key: this.pageKey,
      Title: [
        '',
        [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(100),
        ],
      ],
      ApplicationKey: [this.route.getApplicationKey()],
      DefaultPage: [0],
      IsLandingPage: [false],
      PageType: [],
    });

    if (this.pageDetail) {
      this.form.patchValue({
        Title: this.pageDetail.Title,
        Key: this.pageDetail.Key,
        DefaultPage: this.pageDetail.DefaultPage,
        IsLandingPage: this.pageDetail.IsLandingPage,
      });
    }
  };

  /**
   * Get Form Control
   */
  get formControl() {
    return this.form.controls;
  }

  /**
   * On Submit
   */
  onSubmit = async () => {
    let payload:any = { ...this.form.value, Body: this.body };
    if(!this.form.value?.Title?.trim()){
      this.alert.toast(
        ToastTypes.ERROR,
        'Error',
        'Please enter a Valid Name'
      )
      return
    }
    try {
      this.pageDetail?.Key
        ? await this.action.dispatch(
            OpTypes.UPDATE,
            new UpdatePage(payload),
            getPagesOperations
          )
        : await this.action.dispatch(
            OpTypes.CREATE,
            new AddPage(payload),
            getPagesOperations
          );
      this.close();
    } catch (err) {}
  };

  close = () => {
    if(this.route.previousUrl && this.route.previousUrl !== '/' && this.route.previousUrl !== `apps/${this.route.getApplicationKey()}/settings/user-interface/pages`){
      this.route.back();
    } else {
      this.route.navigate([
        `apps/${this.route.getApplicationKey()}/settings/user-interface/pages`,
      ]);
    }
  };

  ngOnDestroy() {
    this.subsink.unsubscribe();
  }
}
